import {Button} from '@dabafinance/react-components';
import styles from './payouts.module.scss';
import gamer from 'images/gamer.png';
import {Dropdown, Form, Menu, Pagination, Table, Tag, Tooltip} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {GET_DIVIDENDS_PAYOUT_HISTORY} from 'graphql/queries/dividends';
import DBModal from 'components/modal/modal';
import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import dotsIcon from 'icons/dots.svg';
import {useSelector} from 'react-redux';
import {
  CreditOrDebitUserDividend,
  DividendHistory,
  ProcessUserDividend,
  UpdatePayoutAmount,
} from '../create/updateUserDividend/UpdateUserDividend';
import {PAYOUT_ALL_USERS_DIVIDEND} from 'graphql/mutations/dividends';
import {ProcessDividendPayout} from './ProcessModals';
import moment from 'moment';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {useVisibleSubTabs} from 'utils/useVisibleSubTabs';
import UserLink from 'components/userLink/UserLink';

const DIVIDEND_SUBTABS = [
  {key: 'payout', value: 'PAYOUT', label: 'Payout'},
  {key: 'history', value: 'HISTORY', label: 'Payout History'},
];
import SubmitButton from 'components/Startup/components/SubmitButton';
import {InstrumentFields} from './instrumentPicker';
import {RetweetOutlined} from '@ant-design/icons';
import {currencySymbols} from 'utils/mock';

const Payouts = () => {
  const [openPayoutModal, setOpenPayoutModal] = useState(false);
  const [dividendUserId, setDividendUserId] = useState('');
  const [openUpdatePayoutModal, setOpenUpdatePayoutModal] = useState('');
  const [openPayoutAllUsersModal, setOpenPayoutAllUsersModal] = useState(false);
  const [form] = Form.useForm();
  const [processForm] = Form.useForm();

  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState('PAYOUT');
  const [filterValues, setFilterValues] = useState({});
  const [limit, setLimit] = useState(10);
  const [processDividendPayouts, {loading: loadingPayoutAllUsers}] =
    useMutation(PAYOUT_ALL_USERS_DIVIDEND, {
      onError: error => toast.error(`Error something wrong -${error.message}`),
    });
  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Dividends),
  );

  const {visibleSubTabs, visibleKeys, firstVisible} = useVisibleSubTabs(
    NavTabsEnum.Dividends,
    DIVIDEND_SUBTABS,
  );

  useEffect(() => {
    const current = DIVIDEND_SUBTABS.find(sub => sub.value === activeTab);
    if (firstVisible && (!current || !visibleKeys.includes(current.key))) {
      setActiveTab(firstVisible.value);
    }
  }, [firstVisible, visibleKeys, activeTab]);

  const isPayoutPage = activeTab === 'PAYOUT';

  const {data, loading, refetch} = useQuery(GET_DIVIDENDS_PAYOUT_HISTORY, {
    variables: {
      input: {
        page: 1,
        limit: 15,
        status: isPayoutPage ? 'UNPAID' : 'PAID',
      },
    },
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const handlePayoutAllUsers = values => {
    // The issuer company is resolved from the instrument by the service, and
    // `companyId` is rejected outright — instrumentId is the whole selector.
    processDividendPayouts({
      variables: {instrumentId: values.instrumentId},
    })
      .then(({data: {processDividendPayouts}}) => {
        if (processDividendPayouts.__typename === 'Error') {
          toast.error(processDividendPayouts.message);
        } else {
          processForm.resetFields();
          toast.success(processDividendPayouts?.message);
          refetch();
          setOpenPayoutAllUsersModal(false);
        }
      })
      .catch(error => toast.error(error));
  };

  const handleFilter = (values, currentPage, newLimit) => {
    let {
      startDate,
      instrumentId,
      endDate,
      userId,
      email,
      export: exportEmail,
    } = values;
    const instrument = instrumentId ?? filterValues?.instrumentId;
    setFilterValues(prev => ({...prev, instrumentId: instrument}));
    refetch({
      input: {
        status: isPayoutPage ? 'UNPAID' : 'PAID',
        // getDividendHistory rejects companyId/investmentCategory on sight, so
        // an absent instrument filter omits the key rather than sending null.
        ...(instrument && {instrumentId: instrument}),
        userId,
        startDate,
        ...(exportEmail && {emails: [email], export: exportEmail}),
        limit: newLimit ? newLimit : limit,
        endDate,
        page: currentPage ?? page,
      },
    });
    if (exportEmail) {
      form.setFieldsValue({
        export: false,
        email: null,
      });
      setFilterValues({
        ...filterValues,
        export: false,
        email: null,
      });
    }
  };

  const handleChangePage = (currentPage, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setLimit(pageSize);
    setPage(currentPage);
    handleFilter(filterValues, currentPage, pageSize);
  };

  const handleResetPage = () => {
    setFilterValues({});
    setPage(1);
  };

  const columns = [
    {
      dataIndex: 'name',
      title: 'Name',
      key: 'name',
      fixed: 'left',
      render: (text, record) => (
        <div className={styles['record-label']}>
          <div className={styles['record-name']}>
            {record?.user?.imageUrl ? (
              <img src={record?.user?.imageUrl} alt="" />
            ) : (
              <img src={gamer} alt="" />
            )}
            <h4 style={{width: 100}}>
              <UserLink
                userId={record?.user?.id}
                tab="transactions?tab=dividend">
                {record?.user?.firstName} {record?.user?.lastName}
              </UserLink>
            </h4>
          </div>
        </div>
      ),
    },
    {
      dataIndex: 'company',
      title: 'Company',
      key: 'company',
      fixed: isPayoutPage ? '' : 'left',
      render: (text, record) => (
        <div className={styles['record-label']}>
          <img src={record?.company?.logoImgURL} alt="" />
          <h4 style={{width: 100}}>{record?.company?.legalName}</h4>
        </div>
      ),
    },
    {
      dataIndex: 'instrument',
      title: 'Instrument',
      key: 'instrument',
      render: (text, record) => (
        <div className={styles['record-label']}>{record?.instrument?.name}</div>
      ),
    },
    {
      dataIndex: 'investmentCategory',
      title: 'Category',
      key: 'category',
      render: (text, render) => (
        <Tag
          color={
            {
              'BOND': 'cyan',
              'STOCK': 'red',
              'VENTURE': 'orange',
              'IPO': 'green',
            }[render?.investmentCategory]
          }>
          {render?.investmentCategory}
        </Tag>
      ),
    },
    {
      dataIndex: 'units',
      title: 'Number of Shared Units',
      key: 'Number of Shared',
    },
    {
      dataIndex: 'principalPerShare',
      title: 'Principal Per Share',
      key: 'principalPerShare',
      render: (text, render) => (
        <span>
          {currencySymbols[render?.payoutCurrency]?.symbol}{' '}
          {render?.principalPerShare?.toLocaleString()}
        </span>
      ),
    },
    {
      dataIndex: 'totalPrincipalAmount',
      title: 'Total Principal Amount',
      key: 'totalPrincipalAmount',
      render: (text, render) => (
        <span>
          {currencySymbols[render?.payoutCurrency]?.symbol}{' '}
          {render?.totalPrincipalAmount?.toLocaleString() ?? '--'}
        </span>
      ),
    },
    {
      dataIndex: 'totalPrincipalAmount',
      title: 'Total Principal Amount',
      key: 'totalPrincipalAmount',
      render: (text, render) => (
        <span>
          {currencySymbols[render?.payoutCurrency]?.symbol}{' '}
          {render?.totalPrincipalAmount?.toLocaleString() ?? '--'}
        </span>
      ),
    },
    {
      dataIndex: 'amountPerShare',
      title: 'Expected Dividend (Per Share)',
      key: 'amountPerShare',
      render: (text, render) => (
        <span>
          {currencySymbols[render?.payoutCurrency]?.symbol}{' '}
          {render?.amountPerShare?.toLocaleString()}
        </span>
      ),
    },
    {
      dataIndex: 'totalDividendAmount',
      title: 'Total Dividend Amount',
      key: 'totalDividendAmount',
      render: (text, render) => (
        <span>
          {currencySymbols[render?.payoutCurrency]?.symbol}{' '}
          {render?.totalDividendAmount?.toLocaleString() ?? '--'}
        </span>
      ),
    },
    {
      dataIndex: 'payoutAmount',
      title: 'Total Payout Amount',
      key: 'totalPayoutAmount',
      render: (text, render) => (
        <span>
          {currencySymbols[render?.payoutCurrency]?.symbol}{' '}
          {render?.payoutAmount?.toLocaleString()}
        </span>
      ),
    },
    {
      dataIndex: 'taxName',
      title: 'Tax Name',
      key: 'taxName',
      render: (text, render) => <span>{render?.taxName}</span>,
    },
    {
      dataIndex: 'taxPercentage',
      title: 'Tax Percentage',
      key: 'taxPercentage',
      render: (text, render) => <span>{render?.taxPercentage ?? '--'}%</span>,
    },

    {
      dataIndex: 'period',
      title: 'Payout Period',
      key: 'period',
      children: [
        {
          dataIndex: 'startDate',
          title: 'Start Date',
          key: 'startDate',
          render: (text, render) => (
            <span>
              {moment(render?.period?.startDate).format('MMM DD, YYYY')}
            </span>
          ),
        },
        {
          dataIndex: 'endDate',
          title: 'End Date',
          key: 'endDate',
          render: (text, render) => (
            <span>
              {moment(render?.period?.endDate).format('MMM DD, YYYY')}
            </span>
          ),
        },
      ],
    },
    {
      dataIndex: 'status',
      title: 'Status',
      key: 'status',
      render: (text, render) => (
        <Tag
          color={
            {
              'PAID': 'green',
              'UNPAID': 'default',
            }[render?.status] || 'blue'
          }
          key={render.status}>
          {render.status}
        </Tag>
      ),
    },
    {
      dataIndex: 'actions',
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      render: (text, render) => (
        <div>
          <Dropdown
            overlay={
              <Menu>
                {render.status === 'PAID' ? (
                  <>
                    {tabOperations?.update && (
                      <Menu.Item
                        onClick={() => {
                          setDividendUserId({
                            id: render?.id,
                            amount: render?.payoutAmount,
                            currency: render?.payoutCurrency,
                          });
                          setOpenUpdatePayoutModal('CREDIT');
                        }}
                        key="3">
                        Credit / Debit User
                      </Menu.Item>
                    )}
                    <Menu.Item
                      onClick={() => {
                        setDividendUserId({
                          id: render?.id,
                          history: render?.history,
                        });
                        setOpenUpdatePayoutModal('HISTORY');
                      }}
                      key="4">
                      History
                    </Menu.Item>
                  </>
                ) : (
                  tabOperations?.update && (
                    <>
                      <Menu.Item
                        onClick={() => {
                          setDividendUserId({
                            id: render?.id,
                            units: render?.units,
                            principalPerShare: render?.principalPerShare,
                            amount: render?.amountPerShare,
                          });
                          setOpenUpdatePayoutModal('AMOUNT');
                        }}
                        key="1">
                        Edit Dividend Payout
                      </Menu.Item>
                      <Menu.Item
                        onClick={() => {
                          setDividendUserId(render?.id);
                          setOpenUpdatePayoutModal('PROCESS');
                        }}
                        key="2">
                        Pay Individual Dividend
                      </Menu.Item>
                      <Menu.Item
                        onClick={() => {
                          setDividendUserId(render?.id);
                          setOpenUpdatePayoutModal('DELETE');
                        }}
                        key="3">
                        Delete User from Payout List
                      </Menu.Item>
                    </>
                  )
                )}
              </Menu>
            }>
            <img src={dotsIcon} />
          </Dropdown>
        </div>
      ),
    },
  ];

  if (!isPayoutPage) {
    columns.splice(5, 0, {
      dataIndex: 'payoutDate',
      title: 'Payout Date',
      key: 'payoutDate',
      render: (text, render) => (
        <p style={{minWidth: 100}}>
          {moment(render?.payoutDate).format('MMM DD, YYYY')}
        </p>
      ),
    });
  }

  return (
    <div className={styles.root}>
      <div className={styles.top}>
        <h1>Dividend Payout</h1>
        <div>
          {isPayoutPage && (
            <Dropdown
              trigger="hover"
              menu={{
                items: [
                  {
                    key: 1,
                    label: 'Finalize Dividends Payout',
                    onClick: () =>
                      setOpenPayoutModal({isOpen: true, type: 'FINALIZE'}),
                  },
                  {
                    key: 2,
                    label: 'Export Dividends Payout',
                    onClick: () =>
                      setOpenPayoutModal({isOpen: true, type: 'CSV'}),
                  },
                ],
              }}>
              <div>
                <Button
                  className={styles.button}
                  disabled={!tabOperations?.create}
                  label={'Process New Dividend Payout'}
                />
              </div>
            </Dropdown>
          )}
        </div>
      </div>
      <div className={styles['details-toggler']}>
        {visibleSubTabs.map(tab => (
          <span
            key={tab.key}
            className={activeTab === tab.value ? styles['active-toggle'] : ''}
            onClick={() => {
              setActiveTab(tab.value);
              handleResetPage();
            }}>
            {' '}
            {tab.label}
          </span>
        ))}
      </div>
      <div className={styles.table}>
        <div>
          {isPayoutPage ? (
            <div className={styles.header}>
              <h1>Unpaid Payouts</h1>
              <div className={styles.filterHeader}>
                <Button
                  onClick={() => setOpenPayoutAllUsersModal(true)}
                  label="Payout All Users"
                  disabled={!tabOperations?.update}
                />
                <div className={styles.header}>
                  <div />
                  <Button
                    onClick={() =>
                      setOpenPayoutModal({
                        isOpen: true,
                        type: 'UNPAID_SEARCH',
                      })
                    }
                    label="Filter Payouts"
                    type="secondary"
                  />
                </div>
                <Tooltip title="Refresh">
                  <RetweetOutlined
                    size="large"
                    style={{fontSize: 20}}
                    className={styles.refresh}
                    onClick={() => handleFilter(filterValues, 1, limit)}
                  />
                </Tooltip>
              </div>
            </div>
          ) : (
            <div className={styles.header}>
              <div />
              <Button
                onClick={() =>
                  setOpenPayoutModal({isOpen: true, type: 'PAID_SEARCH'})
                }
                label="Filter Payouts"
                type="secondary"
              />
            </div>
          )}
          <div className="ant-table">
            <div className={styles['nav-buttons']}>
              <div className={styles['page-number']}>{page}</div>
            </div>
            <Table
              columns={columns}
              loading={loading}
              pagination={false}
              className="antd-table"
              dataSource={data?.getDividendHistory?.data}
            />
            <Pagination
              current={page}
              onChange={handleChangePage}
              total={data?.getDividendHistory?.pagination?.total}
              showQuickJumper
              className="ant-table-pagination"
              showSizeChanger
            />
          </div>
        </div>
      </div>
      <div>
        <ProcessDividendPayout
          openPayoutModal={openPayoutModal}
          onClose={() => {
            setOpenPayoutModal({isOpen: false, type: null});
            if (
              !['UNPAID_SEARCH', 'PAID_SEARCH'].includes(openPayoutModal?.type)
            ) {
              form.resetFields();
            }
          }}
          activeTab={activeTab}
          resetPage={() => setPage(1)}
          form={form}
          handleFilterValues={setFilterValues}
          onFilter={handleFilter}
        />
        <DBModal
          isOpen={openUpdatePayoutModal}
          handleClose={() => setOpenUpdatePayoutModal('')}
          width={openUpdatePayoutModal === 'HISTORY' ? '1000px' : ''}
          content={
            <div>
              {openUpdatePayoutModal === 'AMOUNT' ? (
                <UpdatePayoutAmount
                  refetch={refetch}
                  data={dividendUserId}
                  closeModal={() => setOpenUpdatePayoutModal('')}
                />
              ) : openUpdatePayoutModal === 'PROCESS' ||
                openUpdatePayoutModal === 'DELETE' ? (
                <ProcessUserDividend
                  id={dividendUserId}
                  refetch={refetch}
                  isDelete={openUpdatePayoutModal === 'DELETE'}
                  closeModal={() => setOpenUpdatePayoutModal('')}
                />
              ) : openUpdatePayoutModal === 'HISTORY' ? (
                <DividendHistory data={dividendUserId} />
              ) : (
                <CreditOrDebitUserDividend
                  data={dividendUserId}
                  refetch={refetch}
                  closeModal={() => setOpenUpdatePayoutModal('')}
                />
              )}
            </div>
          }
        />

        <DBModal
          isOpen={openPayoutAllUsersModal}
          handleClose={() => setOpenPayoutAllUsersModal(false)}
          content={
            <div className={styles.allUser}>
              <div className={styles.top}>
                <h1>Process All Users Dividend</h1>
                <span>
                  Are you sure, you want to Process all Users Dividends Payouts
                </span>
              </div>
              <Form
                layout="vertical"
                form={processForm}
                onFinish={handlePayoutAllUsers}>
                <InstrumentFields form={processForm} required />
                <SubmitButton
                  disabled={loadingPayoutAllUsers}
                  label="Payout All Users"
                />
              </Form>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default Payouts;
